import MainLayout from '@/shared/components/MainLayout';
import Header from '@/shared/components/ui/Header';
import { FileText, Minus, Plus, Calendar, Save, Calculator } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function AddAccountingEntry() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([
        { id: 1, account: '', debit: '', credit: '' },
        { id: 2, account: '', debit: '', credit: '' }
    ]);

    const addRow = () => {
        setRows([...rows, { id: Date.now(), account: '', debit: '', credit: '' }]);
    };

    const removeRow = (id: number) => {
        if (rows.length > 1) {
            setRows(rows.filter(row => row.id !== id));
        }
    };

    return (
        <MainLayout>
            <Header title={t('Add Accounting Entry')} info='' />

            <div className="bg-gray-50/50 min-h-screen py-8 px-4">
                <div className="max-w-4xl mx-auto space-y-6">

                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1">
                            <div className="flex items-center bg-gray-50 rounded-2xl p-3 border border-gray-100 focus-within:border-blue-500 transition-all">
                                <input type="date" className="bg-transparent w-full outline-none text-gray-700 text-sm font-medium cursor-pointer" />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <div className="flex items-start bg-gray-50 rounded-2xl p-3 border border-gray-100 focus-within:border-blue-500 transition-all">
                                <FileText size={18} className="text-gray-400 ml-2 mt-1" />
                                <textarea
                                    className="w-full bg-transparent outline-none text-sm resize-none"
                                    placeholder={t("General Registration Statement")}
                                    rows={1}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="p-6">
                            <div className="space-y-3">
                                {rows.map((row) => (
                                    <div key={row.id} className="group relative grid grid-cols-1 md:grid-cols-12 gap-4 items-center bg-gray-50/50 hover:bg-blue-50/50 p-4 rounded-2xl border border-transparent hover:border-blue-100 transition-all">
                                        <div className="col-span-1 flex justify-center order-last md:order-first">
                                            <button
                                                onClick={() => removeRow(row.id)}
                                                className=" transition-opacity text-red-500 hover:bg-red-100 p-2 rounded-xl"
                                            >
                                                <Minus size={18} />
                                            </button>
                                        </div>

                                        <div className="col-span-5">
                                            <input
                                                type="text"
                                                placeholder={t("Choose an account")}
                                                className="w-full bg-white md:bg-transparent border border-gray-200 md:border-b md:border-t-0 md:border-x-0 md:rounded-none rounded-xl p-3 md:p-1 text-sm outline-none focus:border-blue-500 font-medium"
                                            />
                                        </div>

                                        <div className="col-span-3">
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    placeholder={t("debtor")}
                                                    className="w-full bg-white md:bg-transparent border border-gray-200 md:border-b md:border-t-0 md:border-x-0 md:rounded-none rounded-xl p-3 md:p-1 text-sm outline-none focus:border-blue-500 font-bold text-emerald-600 placeholder:text-gray-300"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-3">
                                            <input
                                                type="number"
                                                placeholder={t("creditor")}
                                                className="w-full bg-white md:bg-transparent border border-gray-200 md:border-b md:border-t-0 md:border-x-0 md:rounded-none rounded-xl p-3 md:p-1 text-sm outline-none focus:border-blue-500 font-bold text-red-500 placeholder:text-gray-300"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={addRow}
                                    className="flex items-center gap-2 text-blue-600 bg-blue-50 hover:bg-blue-100 px-8 py-3 rounded-2xl font-bold transition-all transform hover:scale-105 active:scale-95"
                                >
                                    <Plus size={20} />
                                    <span>{t("Add a new line")}</span>
                                </button>
                            </div>
                        </div>

                        <div className="p-8">
                            <div className="flex flex-col justify-between items-center gap-6">
                                <div className="flex gap-8 items-center">
                                    <div className="space-y-1"> 
                                        <p className="text-slate-400 text-[10px] uppercase font-bold tracking-[2px]">{t("Total debt:")}</p>
                                        <p className="text-2xl font-black text-emerald-400">0.00</p>
                                    </div>
                                    <div className="w-[1px] h-10 bg-slate-700 hidden md:block"></div>
                                    <div className="space-y-1">
                                        <p className="text-slate-400 text-[10px] uppercase font-bold tracking-[2px]">{t("Total creditor:")}</p>
                                        <p className="text-2xl font-black text-red-400">0.00</p>
                                    </div>
                                </div>

                                <button
                                    className="flex items-center gap-2 bg-blue-600 text-white px-3 py-3 rounded-2xl font-bold transition-all transform hover:scale-105 active:scale-95"
                                >
                                    <Save size={22} />
                                    {t("Save accounting entry")}
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </MainLayout>
    );
}